import React, { useEffect, useState } from 'react'
import ModalActionButton from '../../elements/ModalActionButton'
import { useDispatch, useSelector } from 'react-redux'
import { clearUpdatedApplication, offerAdmission } from '../../../store/actions/applicationsActions'
import { ERROR, SET_SUCCESS } from '../../../store/types'
import SelectField from '../../elements/form/SelectField'
import { Switch } from '@headlessui/react'
import DateField from '../../elements/form/DateField'
import moment from 'moment'
import { fetchDownloadables } from '../../../store/actions/downloadablesActions'
import EmptyState from '../../elements/EmptyState'
import Loader from '../../elements/Loader'
import DownloadableCardMinified from '../downloadables/DownloadableCardMinified'

const OfferAdmission = ({application, closeFunction, reload, description}) => {
    const dispatch = useDispatch()
    const downloadablesSelector = useSelector(state => state.downloadables)
    const applicationsSelector = useSelector((state => state.applications))
    const feesSelector = useSelector(state => state.fees)
    const documentsSelector = useSelector(state => state.documents)

    const [requiresAcceptanceFee, setRequiresAcceptanceFee] = useState(false);
    const [offerPayload, setOfferPayload] = useState({
        application: application._id
    });
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        dispatch(fetchDownloadables('', 0, 0))
        if(applicationsSelector.updatedApplication){
            dispatch({
                type: SET_SUCCESS,
                payload: 'provisional offer of admission sent successfully!'
            })
            dispatch(clearUpdatedApplication())
            reload()
            setTimeout(() => {
                closeFunction()
            }, 50);
        }

        return () => {
            
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationsSelector.updatedApplication, closeFunction, dispatch]);

    const [selectedDownloadables, setSelectedDownloadables] = useState([]);

    const toggleDownloadableSelection = (downloadableId) => {
        const temp = [...selectedDownloadables]
        if(temp.includes(downloadableId)){
            temp.splice(temp.indexOf(downloadableId), 1)
        } else {
            temp.push(downloadableId)
        }
        setSelectedDownloadables(temp)
    }

    const validateForm = () => {
        let errors = {}

        if(requiresAcceptanceFee && (!offerPayload.acceptanceFee || offerPayload.acceptanceFee === '')) {
            errors.acceptanceFee = true
        }

        if(!offerPayload.acceptanceDeadline || offerPayload.acceptanceDeadline === '') {
            errors.acceptanceDeadline = true
        }

        if(!offerPayload.offerLetter || offerPayload.offerLetter === '') {
            errors.offerLetter = true
        }

        setValidationErrors(errors)

        return errors
    }

    const pushOffer = () => {
        if (Object.values(validateForm()).includes(true)) {
            dispatch({
                type: ERROR,
                error: {response: {data: {
                    message: 'Please check the highlighted fields'
                }}}
            })
            return
        }

        dispatch(offerAdmission({...offerPayload, ...{downloads: selectedDownloadables}}))

    }
    
    return (
        <div className='w-full'>
            <div className='pb-3 border-b'>
                <p className='text-sm'>{description}</p>
            </div>

            <div className='flex items-start justify-between gap-x-[20px] mt-4 border-secondary border-opacity-50'>
                <div className='w-full'>

                    <div className="w-full flex justify-between gap-x-4">
                        <div className='w-10/12'>
                            <label className="block text-sm font-medium text-gray-700 font-sofia-pro">Requires fee</label>
                            <label className="text-xs text-gray-500 font-sofia-pro">Flip this switch if this examination requires a fee for entry.</label>
                        </div>
                        <div className='w-2/12 flex flex-row-reverse'>
                            <Switch
                                checked={requiresAcceptanceFee}
                                onChange={()=>{setRequiresAcceptanceFee(!requiresAcceptanceFee)}}
                                className={`${
                                requiresAcceptanceFee ? 'bg-accent' : 'bg-gray-400'
                                } relative inline-flex items-center h-5 rounded-full w-10`}
                                >
                                <span className="sr-only">Requires fee</span>
                                <span
                                    className={`transform transition ease-in-out duration-200 ${
                                    requiresAcceptanceFee ? 'translate-x-6 bg-primary' : 'translate-x-1 bg-gray-600'
                                    } inline-block w-3 h-3 transform rounded-full`}
                                />
                            </Switch>
                        </div>

                    </div>

                    {requiresAcceptanceFee && 
                        <div className='mt-4 relative'>
                            {feesSelector?.fees?.fees?.length > 0 ? 
                            <SelectField
                                selectOptions={feesSelector?.fees?.fees.filter(fee => {return fee.feeType === 'PROVISIONAL_OFFER_ACCEPTANCE'})}
                                inputLabel="Select fee"
                                titleField="name"
                                displayImage={false}
                                imageField=""
                                requiredField={true}
                                preSelected={offerPayload.acceptanceFee || ''}
                                preSelectedIndex={''}
                                preSelectedLabel="_id"
                                fieldId="exam-select"
                                hasError={validationErrors && validationErrors.acceptanceFee}
                                // return id of accounts of the selected option
                                returnFieldValue={(value) => {setOfferPayload({...offerPayload, ...{acceptanceFee: value._id}})}}
                            /> 
                            : 
                                <div className='w-full bg-gray-100 p-4 rounded text-xs flex items-center justify-center'>
                                    <p className='text-red-600'>There are no fees yet. To be able to add an acceptance fee to this offer, please navigate to "Fees" on the left sidebar and create one first.</p>
                                </div>
                            }
                        </div>
                    }

                    <div className='my-4 w-full'>
                        <DateField
                            inputLabel="Deadline for acceptance" 
                            fieldId="deadline-date" 
                            requiredField={true}
                            inputType="text" 
                            preloadValue={(moment(offerPayload.acceptanceDeadline).format('YYYY-MM-DD')) || ''}
                            hasError={validationErrors && validationErrors.acceptanceDeadline} 
                            returnFieldValue={(value)=>{
                                const formattedDate = (moment(value, 'YYYY-MM-DD').format('DD-MM-YYYY'))
                                setOfferPayload({...offerPayload, ...{acceptanceDeadline: formattedDate}})
                            }}
                        />
                        <label 
                        className={`text-sm lg:text-xs cursor-text z-10 block relative py-1 transition duration-200 text-gray-400`}
                        >
                        After this date, the offer will become invalid.
                        </label>
                    </div>

                    <div className='mt-4 relative'>
                        {documentsSelector?.documents?.documents?.length > 0 ? 
                        <SelectField
                            selectOptions={documentsSelector?.documents?.documents}
                            inputLabel="Select offer letter"
                            titleField="name"
                            displayImage={false}
                            imageField=""
                            requiredField={true}
                            preSelected={offerPayload.offerLetter || ''}
                            preSelectedIndex={''}
                            preSelectedLabel="_id"
                            fieldId="exam-select"
                            hasError={validationErrors && validationErrors.offerLetter}
                            // return id of accounts of the selected option
                            returnFieldValue={(value) => {setOfferPayload({...offerPayload, ...{offerLetter: value._id}})}}
                        /> 
                        : 
                            <div className='w-full bg-gray-100 p-4 rounded text-xs flex items-center justify-center'>
                                <p className='text-red-600 text-xs'>There are no documents yet. To be able to add an offer letter to this offer, please navigate to "Documents" above and create one first.</p>
                            </div>
                        }
                        <p className='text-gray-600 text-xs mt-[5px]'>The offer letter will be sent to the applicant's parent/guardian right away. This is not the admission letter, but a letter that informs the applicant of all necessary processes they need to complete to gain admission and any other terms of the offer.</p>
                    </div>
                    
                    <div className='mt-4 relative'>
                        {documentsSelector?.documents?.documents?.length > 0 ? 
                        <SelectField
                            selectOptions={documentsSelector?.documents?.documents}
                            inputLabel="Select admission letter"
                            titleField="name"
                            displayImage={false}
                            imageField=""
                            requiredField={true}
                            preSelected={offerPayload.admissionLetter || ''}
                            preSelectedIndex={''}
                            preSelectedLabel="_id"
                            fieldId="exam-select"
                            hasError={validationErrors && validationErrors.admissionLetter}
                            // return id of accounts of the selected option
                            returnFieldValue={(value) => {setOfferPayload({...offerPayload, ...{admissionLetter: value._id}})}}
                        /> 
                        : 
                            <div className='w-full bg-gray-100 p-4 rounded text-xs flex items-center justify-center'>
                                <p className='text-red-600'>There are no documents yet. To be able to add an offer letter to this offer, please navigate to "Documents" above and create one first.</p>
                            </div>
                        }
                        <p className='text-gray-600 text-xs mt-[5px]'>This is the admission letter, and it will be sent to the applicant after they have paid all necessary fees required to gain admission and the student is created</p>
                    </div>
                </div>
                
                <div className='w-full h-[400px] scrollbar-hidden overflow-y-scroll'>
                    <h3 className='text-sm font-[500]'>Required downloads</h3>
                    <p className='text-xs mb-[15px] text-gray-500 mt-[5px]'>Please select some downloadables below to accompany this admission offer. The links to the files will be sent as part of the admission letter emails and also displayed on the application's page.</p>

                    {downloadablesSelector.loadingDownloadables
                    ? 
                        <Loader /> 
                    :
                        downloadablesSelector?.downloadables?.downloadables?.length > 0 ? <>
                            <div className='w-full mt-[10px]'>
                                {downloadablesSelector?.downloadables?.downloadables?.map((downloadable, downloadableIndex) => (
                                    <div 
                                        key={downloadableIndex} 
                                        className={`w-full rounded-[8px] mb-[5px] cursor-pointer p-[5px] border ${selectedDownloadables.includes(downloadable._id) ?'border-vcm-purple bg-vcm-light-purple bg-opacity-10' : 'border-transparent bg-transparent'}`}
                                        onClick={()=>{toggleDownloadableSelection(downloadable._id)}}
                                    >
                                        <DownloadableCardMinified 
                                            downloadable={downloadable}
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                        : 
                        <div className='mt-12'>
                            <EmptyState
                                emptyStateText={`No downloadables created yet. Please create some first if you need to add downloads to this admission`}
                            />
                        </div>
                    }
                </div>
            </div>

            <div className="mt-8 flex flex-row-reverse gap-x-4">
                <ModalActionButton
                    buttonLabel={`Offer Admission`}
                    buttonAction={()=>{pushOffer()}}
                    processing={applicationsSelector.updatingApplication}
                />

                <button
                    type="button"
                    className="inline-flex justify-center px-5 py-4 text-xs font-medium bg-transparent border border-transparent rounded-md hover:bg-opacity-50 hover:bg-secondary focus:outline-none"
                    onClick={closeFunction}
                >
                    Close
                </button>
                
            </div>

        </div>
    )
}

export default OfferAdmission